/**
 * Community — response shapes of DealHunt's `/api/owner/community/*` reads.
 *
 * Mirror, by hand, of dealhunt `shared/ownerCommunityTypes.ts` (contract:
 * dealhunt/docs/sg-owner-community-contract.md). Points, tiers, ranks and founding numbers are
 * DERIVED by DealHunt on every read and arrive here as plain fields: nothing in SoundGuys
 * recomputes them, so the arithmetic lives in one place.
 *
 * Vocabulary: readers see "owner reviews"; the payload keeps the historical `note` name.
 */
import type { CatalogPageType } from 'shared-code/app-events'
import type { OwnerNotePublic, OwnerReadValue } from 'lib/dealhunt/types'
import type {
  DiscussionCard,
  DiscussionIdentityOfThread,
  DiscussionStaffOfThread,
} from './discussions-types'

export type { DiscussionIdentityOfThread }

export const COMMUNITY_TIERS = ['listener', 'contributor', 'expert', 'master', 'reference'] as const
export type CommunityTier = (typeof COMMUNITY_TIERS)[number]

export interface CommunityCategory {
  id: string
  slug: string
  name: string
  /** The taxonomy path ("electronics/audio/headphones") — what the SoundGuys category page is
   *  built from (`dealHuntCategoryHref`). Phase 1c; absent from an older DealHunt, and then the
   *  category is named without a link. */
  full_slug?: string
}

export interface CommunityProduct {
  node_id: string
  name: string
  /** What follows `/product/` on SoundGuys (already brand-prefixed). */
  slug: string
  image_url: string | null
  category: CommunityCategory | null
  /** The SoundGuys review score (0–10) and review article, when reviewed — the same figures as
   *  `CommunityProductStats`, on every product object (feed cards show "SG 7.4"). Absent from a
   *  DealHunt before the thread/feed presentation fields (2026-10-03): nothing is shown. */
  sg_score?: number | null
  sg_review_url?: string | null
  /** The brand node above the product line — "Same brand" beside a thread. Absent from an older
   *  DealHunt; null for a line without one. */
  brand?: { name: string; slug: string } | null
  /** The product line's own taxonomy path
   *  ("electronics/audio/headphones/over-ear-headphones/bose/quietcomfort-35-ii") — what the
   *  category trail is built from (`productCategoryTrail`). Absent from an older DealHunt. */
  full_slug?: string | null
  /** Published owner reviews on this product line — the feed row's "N owner reviews →". Not
   *  sent yet (plan slice 3, DealHunt first); absent = no hint. */
  review_count?: number | null
}

/** One category or subcategory under `electronics/audio` (any depth) with an active product
 *  line — a row of `GET /api/owner/community/categories`. Brand and series nodes are not in it. */
export interface CommunityCategoryNode {
  id: string
  /** The node's own slug segment; the leaderboard's `?category=` takes a top-level one. */
  slug: string
  name: string
  /** "electronics/audio/headphones/over-ear-headphones". */
  full_slug: string
  /** Published owner reviews on product lines beneath the node, rolled up by DealHunt. */
  reviews: number
  /** Published discussions in the node's subtree, rolled up by DealHunt (2026-10-04). Absent from
   *  an older DealHunt: the Discussions pills then show no number. */
  discussions?: number
}

/** `GET /api/owner/community/categories` — ordered by `full_slug`, shared-cached 60 s. */
export interface CommunityCategoriesResponse {
  categories: CommunityCategoryNode[]
}

/** `member` is null for an author who has not signed in: a handle, no public page. */
export interface CommunityAuthor {
  handle: string
  member: { tier: CommunityTier; founding_number: number | null } | null
  /** 2026-10-10 member identity: the shown name (the member's commentHub display name) when the
   *  member shows it and it is usable; ABSENT (never null) when the member keeps the pseudonym, is
   *  hidden / blocked, has not been synced, or on an older DealHunt. Render `Name @handle`
   *  together (`MemberName`); absent → the handle as before. */
  display_name?: string
  /** Google photo (`https://*.googleusercontent.com/…`), sent ONLY beside `display_name`; drawn
   *  by `TierAvatar` with `referrerpolicy="no-referrer"`, the initials disc as its fallback. */
  avatar_url?: string
}

/**
 * Where a feed row's vote and Ask report from (`community_page_owner_vote` / `_ask_open` /
 * `_ask_post` keep their names on every surface): the community feed by default
 * (`FEED_CONTROL_ANALYTICS`); a best list's Owners block sends `guide_page` and its surface
 * (`PickOwners`, contract #217).
 */
export interface FeedControlAnalytics {
  page_type: CatalogPageType
  location: string
}

export const FEED_CONTROL_ANALYTICS: FeedControlAnalytics = {
  page_type: 'community_page',
  location: 'feed',
}

export interface CommunityFeedItem {
  /** Phase 3 adds `question` and `discussion`; a renderer must skip a kind it does not know. */
  kind: 'owner_review'
  note: OwnerNotePublic
  /** Answer ids resolved to display labels by DealHunt, so the feed needs no question set. */
  labels: {
    ownership_length: string
    still_using: string
    reads: Array<{ id: string; label: string; value: OwnerReadValue }>
    /** "Mostly for": the note's `answers.use_cases` as labels, in answer order, unknown ids
     *  dropped by DealHunt. Absent from an older DealHunt — then no cell. */
    use_cases?: string[]
  }
  product: CommunityProduct
  author: CommunityAuthor
  /** Visible replies on the review's commentHub thread, counted by DealHunt from commentHub's
   *  activity feed (phase 2). Read it through `model.ts` `replyCount`. Absent from an older
   *  DealHunt: no reply line. */
  reply_count?: number
  /** Helpful votes on the review (phase 2) — a public figure, not a reader count; the same as
   *  `note.helpful_count`. Read through `helpfulCount`; absent = no Helpful control. */
  helpful_count?: number
  /** The review's Yes / No votes ("Did this help you decide?", contract #201) — public and
   *  shared-cached like `helpful_count` (`yes === helpful_count`). Present exactly when DealHunt
   *  stores the vote's value (#199/#202 deployed): its presence is what switches the feed row from
   *  Helpful to the Yes / No control (`voteCounts`). */
  vote_counts?: CommunityVoteCounts
  /** The feed Ask chips already asked (owner-notes contract #206 and its per-review follow-up):
   *  one row per chip id, public and shared-cached — the "… · asked" / "… · answered" links are in
   *  the HTML. `[]` = none; ABSENT before DealHunt's `owner_discussion_templates` push or from an
   *  older DealHunt: then no chip shows as asked. Read through `askChips` (`ask-rules.ts`), which
   *  ignores an id the chip bank no longer has. */
  asked?: CommunityAskedChip[]
  /** The NEWEST visible reply on the review's thread (owner-notes contract #210, feed plan §2
   *  "Reply preview") — public and shared-cached, the block is in the HTML. Present ONLY when the
   *  thread has a visible reply commentHub could read; ABSENT otherwise (no reply, the newest one
   *  held or deleted, commentHub down or not deployed, an older DealHunt) — never `{}`, never the
   *  next-newest reply. Read through `replyPreviewModel`: absent → nothing rendered. */
  reply_preview?: CommunityReplyPreview
}

/** One reply under a feed row (`OwnerCommunityReplyPreview`, contract #210). */
export interface CommunityReplyPreview {
  reply_id: string
  /** A DealHunt MEMBER replier reads like the thread's `identities`: the shown name + photo when
   *  the member shows them, else `display_name: null` + the handle (never commentHub's name — the
   *  real one). Anyone else reads commentHub's name / photo with `handle: null`. `staff` = in the
   *  thread's `staff_hashes`; `owns` = the member declared the line or has a counted review on it
   *  (both false for a non-member). */
  author: {
    display_name: string | null
    avatar_url: string | null
    handle?: string | null
    staff: boolean
    owns: boolean
  }
  /** Plain text, whitespace collapsed, ≤ 200 code points cut at a word boundary with "…";
   *  mentions and URLs as written. */
  text: string
  created_at: string
  /** The item's `reply_count`. */
  reply_count: number
}

/** One asked feed chip (#206): the chip's bank id, the question's page, has it an answer. */
export interface CommunityAskedChip {
  template_id: string
  /** `/community/discussions/<id>-<slug>` — a root-relative path. */
  url: string
  answered: boolean
}

/** Yes / No votes on an owner review (contract #199/#201). */
export interface CommunityVoteCounts {
  yes: number
  no: number
}

/** The reader's vote on an owner review: Yes, No, or none. */
export type CommunityVoteValue = 'yes' | 'no'

/** The feed's orders. `new` is the default and the one the server renders; `best` (Helpful
 *  desc, then newest) is a READER-chosen order (phase 2, #54). DealHunt also serves `most_read`; SoundGuys does not offer it — a public most-read order
 *  would reveal the view counts that are for signed-in readers only (owner-notes #155). */
export type CommunityFeedSort = 'new' | 'best'

/** `unanswered`: reviews with no reply yet (phase 2). */
export type CommunityFeedFilter = 'unanswered'

export interface CommunityFeedResponse {
  sort: string
  /** Echoed when the feed was filtered (phase 2). */
  filter?: string | null
  /** The category path applied (`?category=`), echoed; null = none or unknown. Absent from a
   *  DealHunt without category feeds — then a category was NOT applied (`servesCategory`). */
  category?: string | null
  page: number
  page_size: number
  total: number
  items: CommunityFeedItem[]
}

export interface CommunityLeaderRow {
  rank: number
  handle: string
  /** All-time tier, whatever period the row was ranked for. */
  tier: CommunityTier
  founding_number: number | null
  reviews: number
  points: number
  /** Most recent product reviewed and how many more: "JBL Tour One M3 +2". */
  gear: { name: string; more: number } | null
  /** Helpful votes received on the member's reviews (phase 2) — the design's column. */
  helpful?: number
  /** Rewarded replies (phase 2). */
  replies?: number
  /** 2026-10-10 member identity: the shown name (the member's commentHub display name) when the
   *  member shows it and it is usable; ABSENT (never null) when the member keeps the pseudonym, is
   *  hidden / blocked, has not been synced, or on an older DealHunt. Render `Name @handle`
   *  together (`MemberName`); absent → the handle as before. */
  display_name?: string
  /** Google photo (`https://*.googleusercontent.com/…`), sent ONLY beside `display_name`; drawn
   *  by `TierAvatar` with `referrerpolicy="no-referrer"`, the initials disc as its fallback. */
  avatar_url?: string
}

/** What each act earns. DealHunt's table: a phase that adds an act adds a key. */
export interface CommunityPoints {
  review: number
  review_update: number
  /** Added to `review` for the EARLIEST currently published review on a product line (phase
   *  1c). Absent from an older DealHunt — then no bonus is promised. */
  first_review_bonus?: number
  /** The photo bonus: paid at publish when the review carries ≥ 1 public photo
   *  (contract-review-photos §4; phase 1c paid it on a moderator's verdict). Key kept. */
  photo_verified?: number
  /** Reviews per UTC day that earn points for one owner (phase 1c); the rest publish and count
   *  for owner stats and Founding, without points. */
  reviews_per_day?: number
  /** A reply on another owner's review (phase 2); a reply on your own review earns 0. */
  reply?: number
  /** Per Helpful vote received on a counted review (phase 2), no daily cap. */
  helpful?: number
  /** Replies per UTC day that earn points for one owner (phase 2). */
  replies_per_day?: number
  /** Early Discussions (2026-10-04): what the Conversation Starter / First Responder badge adds,
   *  once. Absent from an older DealHunt — then the hero cards print no points. */
  conversation_starter?: number
  first_responder?: number
}

/** The Founding Member thresholds — DealHunt's `OWNER_FOUNDING_MEMBER_RULE`, never held here. */
export interface CommunityFoundingMemberRule {
  reviews: number
  replies: number
  helpful: number
}

export interface CommunitySummaryResponse {
  founding: { count: number; cap: number }
  /** Founding Member numbers assigned, out of `cap`, and the badge's rule (the three needs).
   *  Absent from an older DealHunt. */
  founding_member?: { count: number; cap: number; rule: CommunityFoundingMemberRule }
  reviews_total: number
  products_total: number
  /** "2026-10", UTC. */
  month: string
  top_month: CommunityLeaderRow[]
  /** All-time top three — shown while `top_month` is empty (the first days of a month).
   *  Absent from a DealHunt older than phase 1b. */
  top_all?: CommunityLeaderRow[]
  /** Top three over the last three calendar months — the leaderboard's `3m`; absent from an older
   *  DealHunt. */
  top_3m?: CommunityLeaderRow[]
  /** Empty when DealHunt has no honest relevance signal — the block is then not rendered. */
  waiting_products: CommunityProduct[]
  points: CommunityPoints
  /** Published reviews without a reply yet — the "Be the first to reply" tab (phase 2). */
  unanswered_total?: number
  /** Published discussions (questions and discussions, phase 3) — the Discussions tab's count.
   *  Absent from an older DealHunt: the tab shows no count. */
  discussions_total?: number
  /** Early Discussions (2026-10-04) — DealHunt's call that the discussions are still few
   *  (`discussions_total` under its `OWNER_DISCUSSIONS_EARLY_MAX`; SoundGuys never holds the
   *  number): the tab reads NEW instead of a count, the sort row is hidden, the main column lists
   *  the threads waiting for a first reply. Absent or false = the regular page. */
  discussions_early?: boolean
  /** Published discussions of EITHER kind with no answer yet — "N waiting for a first reply" in
   *  the Early Discussions hero (the list's `unanswered_total` stays questions-only). Absent =
   *  no line. */
  discussions_unanswered_total?: number
  /** The two Early Discussions badges, numbered and capped like `founding`: how many numbers
   *  have been given, out of DealHunt's cap. Both absent until DealHunt's tables exist; the hero
   *  renders only when BOTH arrive. */
  conversation_starter?: { count: number; cap: number }
  first_responder?: { count: number; cap: number }
  /** The points of the LAST ranked member (the lowest with points > 0) all-time and this month;
   *  null when that board has nobody (DealHunt 04d96dfc / 8e66e754, Authority-Media/dealhunt#335).
   *  DISPLAY ONLY — the Overview's "Last place on the board" line; never an entry threshold: any
   *  points rank. Absent from an older DealHunt — no line. */
  last_ranked_points?: { all: number | null; month: number | null }
  /** Posts that went public (counted reviews + visible discussions + visible replies) in the
   *  rolling last 24 h (`day`) and 7 days (`week` ⊇ `day`), to `as_of` (contract #215) — the
   *  Community entry's count pill (`communityPill`). ABSENT, never 0, from an older DealHunt, before
   *  its push or when a count fails: no pill. */
  activity?: CommunityActivity
}

export interface CommunityActivity {
  day: number
  week: number
  as_of: string
}

/** `3m` = the current UTC month plus the two before (DealHunt 2026-10). An older DealHunt answers
 *  `3m` with its Month board and echoes `month` — the board shows under the period it echoes. */
export type CommunityPeriod = 'month' | '3m' | 'all'

export interface CommunityLeaderboardResponse {
  period: CommunityPeriod
  month: string
  /** The category PATH applied (any depth since 2026-10-03), echoed. An older DealHunt echoes a
   *  top-level slug only and answers a deeper path unfiltered with `null`
   *  (`boardServesCategory`). */
  category: string | null
  page: number
  page_size: number
  ranked_total: number
  /** `rows[0]` is the node's leader for the period. */
  rows: CommunityLeaderRow[]
  tiers: Array<{ id: CommunityTier; min_points: number; members: number }>
  /** The CHILDREN of the applied node (the top-level categories on the global board). An older
   *  DealHunt sends the top-level list whatever was asked (`childSeats`). */
  category_leaders: CommunityCategoryLeader[]
  points: CommunityPoints
}

export type CommunityBadgeId =
  /** Copy: "Early Owner" — one of the first 100 signed-in owners with a published review. */
  | 'founding_owner'
  /** "Founding Member" (2026-10-03): reviews, replies and Helpful received, numbered, capped. */
  | 'founding_member'
  | 'first_review'
  | 'review_trio'
  | 'long_haul_owner'
  | 'six_month_update'
  | 'cross_category'
  | 'top3_month'
  /** Phase 2: at least one rewarded reply. */
  | 'first_reply'
  /** Phase 2: "Helpful" — 10 Helpful votes received. */
  | 'helpful_10'
  /** Early Discussions (2026-10-04): the author of one of the first discussions that got a reply
   *  from someone else — numbered and capped like `founding_member`, earned iff a stored number. */
  | 'conversation_starter'
  /** Early Discussions: the author of one of the first discussion replies marked helpful —
   *  numbered and capped. */
  | 'first_responder'

/** One step toward a badge, as DealHunt counts it (`reviews` / `replies` / `helpful` for Founding
 *  Member; `categories` / `updates` on the countable badges since DealHunt db2aac16). `replies` and
 *  `helpful` are absent until DealHunt's phase-2 tables exist — a missing step is unknown, never
 *  zero; an unknown key is ignored (`badgeProgressRows`). */
export interface CommunityProgressStep {
  key: 'reviews' | 'replies' | 'helpful' | 'categories' | 'updates'
  have: number
  need: number
}

export interface CommunityBadge {
  id: CommunityBadgeId
  earned: boolean
  /** DealHunt's line, printed as sent: "#3 of 100", "1 / 3 reviews · 0 / 5 replies · …". */
  detail: string
  /** The stored number, on `founding_owner` / `founding_member` once earned. */
  number?: number
  /** ISO. The founding two always; every other earned badge since DealHunt db2aac16, when the
   *  instant is derivable (`long_haul_owner` and `top3_month` never). Absent = no date shown. */
  earned_at?: string
  /** `founding_member` always; the countable badges since DealHunt db2aac16 (`first_reply` and
   *  `helpful_10` only once the phase-2 tables exist). Absent = DealHunt's `detail` line. */
  progress?: CommunityProgressStep[]
  /** ISO. NOT SENT YET — reserved for a time-gated badge DealHunt cannot award before a date (a
   *  six-month update window). When it arrives on an unearned badge, the status under the tile
   *  reads "Opens <Mon D>" (`badgeStatus`); SoundGuys never computes the date itself. */
  opens_at?: string
}

export interface CommunityMemberResponse {
  handle: string
  /** 2026-10-10 member identity: the shown name (the member's commentHub display name) when the
   *  member shows it and it is usable; ABSENT (never null) when the member keeps the pseudonym, is
   *  hidden / blocked, has not been synced, or on an older DealHunt. Render `Name @handle`
   *  together (`MemberName`); absent → the handle as before. */
  display_name?: string
  /** Google photo (`https://*.googleusercontent.com/…`), sent ONLY beside `display_name`; drawn
   *  by `TierAvatar` with `referrerpolicy="no-referrer"`, the initials disc as its fallback. */
  avatar_url?: string
  tier: CommunityTier
  points: number
  /** `replies` / `helpful`: points from rewarded replies and Helpful votes received (phase 2);
   *  `conversation_starter` / `first_responder`: the Early Discussions badge bonuses (2026-10-04).
   *  The parts sum to `points` on DealHunt's side; SoundGuys adds nothing up. */
  points_breakdown: {
    reviews: number
    review_updates: number
    replies?: number
    helpful?: number
    conversation_starter?: number
    first_responder?: number
  }
  next_tier: { id: CommunityTier; points_to_go: number } | null
  founding_number: number | null
  /** ISO. The first publish for an owner; for a member without a review, DealHunt's fallback
   *  (the claim date, 2026-10-04) — printed as sent, never derived here. */
  joined_at: string
  rank_month: number | null
  rank_all: number | null
  /** `replies`: rewarded replies (phase 2); `helpful`: Helpful votes received; `discussions`:
   *  published discussions and questions; `replies_all`: every reply on a visible thread,
   *  rewarded or not (2026-10-07). The last two are absent from an older DealHunt. */
  counts: {
    reviews: number
    products: number
    replies?: number
    helpful?: number
    discussions?: number
    replies_all?: number
  }
  /** The Founding Member number; null = not earned. Absent from an older DealHunt. */
  founding_member_number?: number | null
  badges: CommunityBadge[]
  reviews: CommunityFeedItem[]
  /** The member's published discussions and questions, newest first, up to 5 (2026-10-07).
   *  Absent from an older DealHunt — unknown, never "none". */
  discussions?: CommunityMemberDiscussion[]
  /** The member's latest replies on visible threads (reviews and discussions), newest first, up
   *  to 5 (2026-10-07). Absent from an older DealHunt. */
  replies?: CommunityMemberReply[]
}

/** One of the member's discussions, as the public member payload sends it. */
export interface CommunityMemberDiscussion {
  id: string
  /** Site path, `/community/discussions/<id>-<slug>`. */
  url: string
  kind: 'question' | 'discussion'
  title: string
  /** DealHunt's cached count — it can lag a fresh answer. */
  answer_count: number
  published_at: string
}

/** One of the member's replies: when, and the thread it went to. */
export interface CommunityMemberReply {
  at: string
  thread: { kind: 'review' | 'discussion'; title: string; url: string }
}

/**
 * The reader's OWN standing — on `/me`, on the claim response and (a subset) on the publish
 * response. Per reader and uncached, so it is never stale the way the shared-cached member page
 * can be for a few minutes after a publish. Absent from a DealHunt older than phase 1b.
 */
export interface CommunityStanding {
  /** Signed in (claimed), not blocked, public profile on — review or not (Guillaume,
   *  2026-10-04): every member has a page and a place on the boards; owner reviews are one way
   *  to score beside replies, Helpful votes and badges. */
  member: boolean
  profile_public: boolean
  /** Founding numbers go to signed-in owners only; null for anyone else. */
  founding_number: number | null
  /** Founding numbers still unassigned; 0 once all are taken. */
  founding_spots_left: number
  points: number
  tier: CommunityTier
  next_tier: { id: CommunityTier; points_to_go: number } | null
  rank_month: number | null
  rank_all: number | null
  /** The founding number the reader WOULD take by publishing now — set while spots are left
   *  and the reader is signed in without one; null otherwise (phase 1c). */
  founding_next?: number | null
  /** Reviews that can still earn points today (UTC); 0 = the daily cap is reached (phase 1c). */
  reviews_today_left?: number
  /** When the founding (Early Owner) number was assigned, ISO. Absent from an older DealHunt. */
  founding_assigned_at?: string | null
  /** The reader's way to Founding Member. Absent from an older DealHunt — no card then. */
  founding_member?: CommunityFoundingMemberStanding
  /** Points on the way (2026-10-04): the Conversation Starter bonus the reader's own discussions
   *  will earn once someone else replies. Absent from an older DealHunt — then nothing renders
   *  (`gamification.ts` `conversationStarterPending`). */
  conversation_starter_pending?: CommunityConversationStarterPending
}

export interface CommunityConversationStarterPending {
  /** Signed in (claimed), not blocked, not staff — public OR hidden profile (DealHunt
   *  `earlyNumberProfileOpen`; a hidden profile sees it in My gear) — no Conversation Starter
   *  number yet, numbers left. */
  eligible: boolean
  /** The bonus (DealHunt's scoring constant) — never a literal on this side. */
  points: number
  /** The reader's own discussions waiting for a first reply from someone else. */
  waiting_total: number
  /** ≤ 3, newest first — the same items as the "Waiting for a first reply" list (`scope=all`).
   *  May be [] when `eligible` is false. */
  waiting: DiscussionCard[]
}

export interface CommunityFoundingMemberStanding {
  /** Set once earned. */
  number: number | null
  earned_at: string | null
  spots_left: number
  progress: CommunityProgressStep[]
}

/** On an accepted publish: what THIS publish earned. */
export interface CommunityPublishStanding {
  member: boolean
  founding_number: number | null
  /** This publish assigned the founding number. */
  founding_new: boolean
  founding_spots_left: number
  /** For this publish: a review, a six-month update, or 0 for a plain edit. */
  points_awarded: number
  points: number
  tier: CommunityTier
  /** Set when this publish moved the author up a tier. */
  tier_reached: CommunityTier | null
  /** `points_awarded` includes the first-owner bonus (phase 1c). */
  first_review_bonus?: boolean
  /** The daily cap was reached: published and counted, `points_awarded` is 0 (phase 1c). */
  capped?: boolean
  /** Phase 1c: a private proof photo waited for a moderator. False once a public photo paid the
   *  bonus at publish (contract-review-photos §4); SG no longer prints a pending line. */
  photo_verified_pending?: boolean
  /** `points_awarded` already includes the photo bonus (+`photo_verified`): this publish carried
   *  ≥ 1 public photo (contract-review-photos §4). Absent from an older DealHunt — then SG
   *  falls back to "photos were attached". */
  photo_bonus?: boolean
  /** DealHunt's own sentence about the points of this publish (the capped case, the pending
   *  photo) — printed verbatim when present, over any wording of ours (phase 1c). */
  points_note?: string | null
}

// ── phase 1c: review threads, product hubs, picker counts ────────────────────

/** A product line's owner figures, on the thread and the hub. */
export interface CommunityProductStats {
  /** Published owner reviews on this product line. */
  published_count: number
  /** Published reviews still needed before the product page shows owner percentages (≥ 0). */
  to_percentages: number
  /** SoundGuys review score (0–10), if reviewed. */
  sg_score: number | null
  /** The SoundGuys review article, if any. */
  sg_review_url: string | null
  /** Owner figures for the thread's "About this gear", from the product page's aggregate; null
   *  while the product is below the owner-stats gate. Absent from an older DealHunt. */
  aggregate?: {
    buy_again_pct: number | null
    reads: Array<{ id: string; label: string; better: number; expected: number; worse: number }>
  } | null
}

/** `GET /api/owner/community/reviews/:noteId` — one published owner review: "the thread". */
/**
 * `GET /api/owner/community/reviews?ids=<id1>,<id2>,…` (DealHunt contract #216): 1..60 note ids;
 * each item is EXACTLY a `/feed` item, in the order asked; unknown or uncounted ids are dropped
 * silently. 400 `{ error: "bad_ids" }` on empty, > 60 or malformed. Public, 60 s cache.
 */
export interface CommunityReviewsByIdsResponse {
  items: CommunityFeedItem[]
  generated_at: string
}

export interface CommunityReviewResponse {
  item: CommunityFeedItem
  /** What this review currently earns ITS AUTHOR — not what a new review would earn. */
  earns: {
    review: number
    first_review_bonus: number
    review_update: number
    /** Phase 2: +points × this review's Helpful votes. */
    helpful?: number
  }
  product_stats: CommunityProductStats
  /** The author's public standing when a member; null when not (unclaimed / hidden). */
  author_standing: {
    tier: CommunityTier
    points: number
    rank_month: number | null
    badges_earned: CommunityBadgeId[]
    /** Published reviews, the member page's `counts.reviews`. Absent from an older DealHunt. */
    reviews?: number
  } | null
  /** Up to 2 other reviews in the same top category by OTHER authors, newest first. */
  similar: CommunityFeedItem[]
  /** The other reviews on THIS product, newest first, up to 5 (the hub has them all). */
  siblings: CommunityFeedItem[]
  /** 2026-10-10 member identity (contract B.2/E.7): how to draw commentHub reply authors, by
   *  `authorSubjectHash` — `shown` → name (+ photo), `hidden` → handle + initials, an absent hash
   *  → commentHub's own. Best effort; absent from an older DealHunt (= commentHub's own for all). */
  identities?: Record<string, DiscussionIdentityOfThread>
  /** 2026-10-16 SoundGuys team on the review thread: the team's subject hashes (the discussion
   *  thread's `staff_hashes`, same producer). Since 2026-10-23 SoundGuys reads nothing from it: the
   *  SOUNDGUYS TEAM pill on a reply is commentHub's role (`teamRoleBadge`). Kept in the type as
   *  DealHunt's contract field. */
  staff_hashes?: Record<string, DiscussionStaffOfThread>
}

/** `GET /api/owner/community/products/:slug?page=` — a product's hub, newest first. */
export interface CommunityProductResponse {
  product: CommunityProduct
  product_stats: CommunityProductStats
  page: number
  page_size: number
  total: number
  items: CommunityFeedItem[]
}

/** `GET /api/owner/community/counts?node_ids=` (≤ 12 ids) — for the picker rows. */
export interface CommunityCountsResponse {
  /** Canonical node id → published reviews. Unknown ids are omitted: a missing key is
   *  "unknown", never zero. */
  counts: Record<string, number>
  points: CommunityPoints
}

// ── phase 2: Helpful on a review ─────────────────────────────────────────────

/** `PUT` / `DELETE /api/owner/notes/:id/helpful` and `GET …/helpful/me` (keyed, the reader's
 *  session; DealHunt `OwnerHelpfulResponse`) — the review's count and whether the reader's vote
 *  stands. The ONLY place `mine` is answered. */
export interface CommunityHelpfulAnswer {
  helpful: number
  mine: boolean
  /** The reader's vote (#199): `"yes"`, `"no"`, null = none. Absent from a DealHunt before the
   *  vote's value — then `mine` alone says it (`helpfulValue`). */
  value?: CommunityVoteValue | null
  /** The review's live Yes / No counts (#199); absent from an older DealHunt. */
  vote_counts?: CommunityVoteCounts
}

// ── scoped leaderboards (any category depth, 2026-10-03) ─────────────────────

/** The node a scoped board ranks — on `CommunityLeaderboardResponse` (merged below). */
export interface CommunityLeaderboardNode {
  id: string
  slug: string
  name: string
  full_slug: string
  /** Published owner reviews beneath the node, rolled up by DealHunt. */
  reviews: number
}

export interface CommunityLeaderboardResponse {
  /** The applied node; null on the global board. Absent from an older DealHunt. */
  node?: CommunityLeaderboardNode | null
}

/** One child category's leader on a board. `handle: null` = Open. */
export interface CommunityCategoryLeader {
  category: CommunityCategory
  handle: string | null
  /** The leader's points in the board's period; absent from an older DealHunt. */
  points?: number
  /** Published owner reviews beneath the child, rolled up; absent from an older DealHunt. */
  reviews?: number
}

// ── the member hover card (2026-10-04) ───────────────────────────────────────

/**
 * `GET /api/community/members/:handle` — SoundGuys' own trimmed, edge-cached projection of
 * DealHunt's member read (`memberCardProjection`), for the hover card on author lines. Public
 * figures only, every one DealHunt's; no review text, no reader/view counts (#155).
 */
/** One "Owns and reviewed" row on the member hover card (Amendment B.3). */
export interface CommunityMemberCardGear {
  name: string
  /** The category's name ("Headphones"); null when DealHunt sent none. */
  category: string | null
  /** DealHunt's ownership-length label ("6–12 months"); null when none. */
  ownership: string | null
  /** A public photo (image_count > 0) or a verified photo. */
  photo: boolean
  /** `answers.buy_again`; null when unreadable. */
  buy_again: boolean | null
  /** The product's DealHunt thumbnail, as the feed row draws it; null when none. ABSENT from a
   *  card answer projected before 2026-10-16 (edge/browser caches keep one ~20 min) — then the
   *  tile draws its category letters. */
  image_url?: string | null
}

export interface CommunityMemberCardResponse {
  handle: string
  tier: CommunityTier
  points: number
  founding_number: number | null
  founding_member_number: number | null
  /** `replies_all` (every reply on a visible thread) only when DealHunt sent it. */
  counts: { reviews: number; products: number; replies_all?: number }
  rank_all: number | null
  /** Amendment B.3 (2026-10-11): the progress bar's target; null = top tier. ABSENT from a card
   *  answer projected before v2 (edge/browser caches keep one ~20 min) — then no bar, no line. */
  next_tier?: { id: CommunityTier; points_to_go: number } | null
  /** Amendment B.3: Σ `views_total` over the member's published reviews — public since the Stats
   *  tab (contract §0.1 supersedes #155 here). Absent from a pre-v2 answer → "—". */
  readers_total?: number
  /** Amendment B.3 "Owns and reviewed": the ≤ 2 most-read reviews, pre-labelled; no text, no
   *  per-review counts. Absent from a pre-v2 answer → the block is not drawn. */
  gear?: CommunityMemberCardGear[]
  /** EARNED badges this build knows, Early Owner and Founding Member first. */
  badges: Array<{ id: CommunityBadgeId; earned_at?: string }>
  /** Every badge this build knows, earned or not — the "3 of 10". */
  badges_total: number
  joined_at: string | null
  /** The Early Owner cap as DealHunt wrote it in the badge's line (`badgeCap`); absent when the
   *  member payload carried none — the mini hexagon then prints its digit-free mark. */
  founding_cap?: number
}

// ── the category product index (feed plan slice 5, contract #211/#212) ──────

/**
 * `GET /api/owner/community/products/index?category=<path>` — a category's "By product" view in
 * ONE call (`OwnerCommunityProductIndexResponse`): every product line beneath the category with a
 * counted owner review (uncapped, newest review first), and the "No owner review yet" tail. Public,
 * shared-cached 60 s. A path naming no category is a 404 (`category_not_found`) → `null`.
 */
export interface CommunityProductIndexResponse {
  /** The node applied — `full_slug` is the echo SoundGuys checks (`servesCategory`); null = the
   *  site-wide index (no `?category=`, the community home): products over every category. */
  category: (CommunityCategory & { full_slug: string }) | null
  /** EVERY product line beneath the category (any depth) with ≥ 1 counted owner review —
   *  uncapped — newest `latest_review_at` first, then node id. SG re-sorts client-side for
   *  "Most reviewed". */
  products: CommunityProductIndexLine[]
  /** "No owner review yet": the category's SoundGuys-reviewed lines with no counted owner review,
   *  at most 50 (`OWNER_COMMUNITY_WAITING_INDEX_MAX`), in DealHunt's waiting order (#212). */
  waiting: CommunityWaitingLine[]
  /** How many lines qualify for `waiting` before the cap — the tail heading's "· N products". */
  waiting_total: number
  /** ISO instant the response was built. */
  generated_at: string
}

/** One product line of the index (`OwnerCommunityProductIndexLine`). */
export interface CommunityProductIndexLine {
  /** The feed item's own projection (`review_count` = the hub's `product_stats.published_count`). */
  product: CommunityProduct & { review_count: number }
  /** The product page's figure: null below the owner-stats gate, with no aggregate row, or on a
   *  failed read — then no "would buy again" fact. */
  buy_again_pct: number | null
  /** Visible discussions naming this line — what `/discussions?product=<node_id>` totals. ABSENT
   *  (never a made-up 0) before the Discussions push or when that count failed: no fact. */
  discussion_count?: number
  /** The line's 1–3 newest counted reviews (note ids, the hub's order) — the condensed minis. */
  newest_review_ids: string[]
  /** ISO publish instant of `newest_review_ids[0]`. */
  latest_review_at: string
}

/** One "No owner review yet" product (`OwnerCommunityWaitingLine`). */
export interface CommunityWaitingLine {
  /** `sg_score` / `sg_review_url` ride here (non-null unless DealHunt's SG lookup failed). */
  product: CommunityProduct
  /** SoundGuys readers of the product page this month — ALWAYS null today: DealHunt holds no
   *  SoundGuys page-view counter (#212). Never rendered while null ("readers this month"). */
  views_month: number | null
}
